/**
 * Client-side signature help for function / procedure calls.
 *
 * Why this exists: SQL Tools Service 5.0.20250321.1 answers
 * `textDocument/signatureHelp` with `{signatures: null}` for every input, so
 * the bundled LSP never surfaces argument hints for user-typed calls like
 * `SELECT dbo.ufnGetProductListPrice(`. This module computes the same hints
 * directly from the database's catalog (sys.parameters on MSSQL) via the
 * `get-routine-metadata` IPC and its lazy resolver.
 *
 * Parsing strategy: scan backwards from the cursor to find the innermost
 * unmatched open paren that is NOT inside a string literal or line/block
 * comment, then walk backward from that paren to extract the
 * `[schema.]name` identifier that precedes it. Once we know the routine, we
 * count top-level commas between the open paren and the cursor to determine
 * the active parameter index.
 *
 * Return shape is deliberately bare (`{ signatures, activeSignature,
 * activeParameter }`). The caller in `SqlMonaco.tsx` wraps it in
 * `{ value, dispose }` before returning it to Monaco.
 */

import type { DatabaseEngineId } from '@/core/engines';
import type { RoutineMetadata, RoutineParameter } from '../../../../types/electron';
import { resolveDefaultSchemaForEngine } from './defaultSchemaForEngine';
import { fetchRoutine, getCachedRoutine } from './routineParameterResolver';
import { SQLITE_BUILTIN_ROUTINES } from './sqliteBuiltinRoutines';
import { MSSQL_BUILTIN_ROUTINES } from './mssqlBuiltinRoutines';

export type FunctionSignatureHelp = {
  signatures: Array<{
    label: string;
    documentation?: string;
    parameters: Array<{ label: [number, number]; documentation?: string }>;
  }>;
  activeSignature: number;
  activeParameter: number;
};

type FunctionCallContext = {
  schema: string | null;
  name: string;
  /** Oracle three-level identifier: SCHEMA.PACKAGE.PROC — packageName is the middle segment. */
  packageName?: string | null;
  /** Byte offset of the open paren that the cursor is currently inside. */
  openParenOffset: number;
};

/**
 * Try to read a dollar-quote tag starting at position `pos`. Returns the full
 * tag (`$$` or `$tag$`) if found, otherwise `null`. A tag is `$` followed by
 * zero or more identifier chars and then another `$`.
 */
function readDollarTag(text: string, pos: number): string | null {
  if (text[pos] !== '$') return null;
  let end = pos + 1;
  // Read optional tag identifier (letters, digits, underscore)
  while (end < text.length && /[A-Za-z0-9_]/.test(text[end])) end++;
  if (end >= text.length || text[end] !== '$') return null;
  return text.slice(pos, end + 1); // includes the closing '$'
}

/**
 * Walk backwards from the cursor to find the function-call context the cursor
 * is currently inside. Returns null when the cursor is not inside a
 * parenthesised call, or when no identifier immediately precedes the open
 * paren (e.g. `SELECT (1 +` — a grouping paren, not a call).
 *
 * This parser is intentionally forgiving: it handles string literals
 * (`'...'`, `"..."`, `N'...'`), line comments (`-- ...`), and block comments
 * (`/* ... *\/`) correctly, but skips other SQL esoterica. For signature
 * help it is more important to NEVER attach hints to the wrong call than to
 * cover every edge case.
 */
export function findEnclosingFunctionCall(
  text: string,
  cursorOffset: number,
  engineId?: DatabaseEngineId,
): FunctionCallContext | null {
  if (cursorOffset < 0 || cursorOffset > text.length) return null;

  // Build a "mask" of which character positions are inside a string literal
  // or comment. We walk forward once so we don't have to reason about context
  // during the backward scan.
  const inCommentOrString = new Uint8Array(text.length);
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    const next = text[i + 1];

    // Line comment: -- ... \n
    if (ch === '-' && next === '-') {
      while (i < text.length && text[i] !== '\n') {
        inCommentOrString[i] = 1;
        i++;
      }
      continue;
    }

    // Block comment: /* ... */ (non-nesting for simplicity)
    if (ch === '/' && next === '*') {
      inCommentOrString[i] = 1;
      inCommentOrString[i + 1] = 1;
      i += 2;
      while (i < text.length - 1 && !(text[i] === '*' && text[i + 1] === '/')) {
        inCommentOrString[i] = 1;
        i++;
      }
      if (i < text.length) {
        inCommentOrString[i] = 1;
        inCommentOrString[i + 1] = 1;
        i += 2;
      }
      continue;
    }

    // Single-quoted string: '...' with '' as escape
    if (ch === "'") {
      inCommentOrString[i] = 1;
      i++;
      while (i < text.length) {
        if (text[i] === "'" && text[i + 1] === "'") {
          inCommentOrString[i] = 1;
          inCommentOrString[i + 1] = 1;
          i += 2;
          continue;
        }
        inCommentOrString[i] = 1;
        if (text[i] === "'") {
          i++;
          break;
        }
        i++;
      }
      continue;
    }

    // Double-quoted identifier (treated same as string for paren matching).
    if (ch === '"') {
      inCommentOrString[i] = 1;
      i++;
      while (i < text.length && text[i] !== '"') {
        inCommentOrString[i] = 1;
        i++;
      }
      if (i < text.length) {
        inCommentOrString[i] = 1;
        i++;
      }
      continue;
    }

    // Bracketed identifier: [foo] — treat as string for paren matching.
    if (ch === '[') {
      inCommentOrString[i] = 1;
      i++;
      while (i < text.length && text[i] !== ']') {
        inCommentOrString[i] = 1;
        i++;
      }
      if (i < text.length) {
        inCommentOrString[i] = 1;
        i++;
      }
      continue;
    }

    // Backtick-quoted identifier (MySQL): `name`
    if (ch === '`') {
      inCommentOrString[i] = 1;
      i++;
      while (i < text.length && text[i] !== '`') {
        inCommentOrString[i] = 1;
        i++;
      }
      if (i < text.length) {
        inCommentOrString[i] = 1;
        i++;
      }
      continue;
    }

    // Dollar-quoted string (PostgreSQL): $$...$$ or $tag$...$tag$
    if (ch === '$') {
      const tag = readDollarTag(text, i);
      if (tag !== null) {
        const closer = text.indexOf(tag, i + tag.length);
        const end = closer === -1 ? text.length : closer + tag.length;
        while (i < end) {
          inCommentOrString[i] = 1;
          i++;
        }
        continue;
      }
    }

    i++;
  }

  // Find the innermost unmatched open paren at or before the cursor.
  let depth = 0;
  let openParenOffset = -1;
  for (let j = cursorOffset - 1; j >= 0; j--) {
    if (inCommentOrString[j]) continue;
    const ch = text[j];
    if (ch === ')') {
      depth++;
    } else if (ch === '(') {
      if (depth === 0) {
        openParenOffset = j;
        break;
      }
      depth--;
    }
  }

  if (openParenOffset === -1) return null;

  // Skip whitespace between the identifier and the open paren.
  let k = openParenOffset - 1;
  while (k >= 0 && /\s/.test(text[k])) k--;

  // Extract the trailing identifier. We accept `name`, `[name]`, or `"name"`.
  const nameResult = readIdentifierBackward(text, k);
  if (!nameResult) return null;
  const { identifier: name, startOffset: nameStart } = nameResult;
  if (!name) return null;

  // Look for an optional schema prefix: `schema.name`.
  let schema: string | null = null;
  let packageName: string | null = null;
  let m = nameStart - 1;
  while (m >= 0 && /\s/.test(text[m])) m--;
  if (m >= 0 && text[m] === '.') {
    m--;
    while (m >= 0 && /\s/.test(text[m])) m--;
    const schemaResult = readIdentifierBackward(text, m);
    if (schemaResult) {
      schema = schemaResult.identifier;

      // Oracle three-level identifier: SCHEMA.PKG.PROC
      if (engineId === 'oracle') {
        let p = schemaResult.startOffset - 1;
        while (p >= 0 && /\s/.test(text[p])) p--;
        if (p >= 0 && text[p] === '.') {
          p--;
          while (p >= 0 && /\s/.test(text[p])) p--;
          const ownerResult = readIdentifierBackward(text, p);
          if (ownerResult) {
            packageName = schema;   // middle segment is the package
            schema = ownerResult.identifier; // outer segment is the owner
          }
        }
      }
    }
  }

  return { schema, name, ...(packageName ? { packageName } : {}), openParenOffset };
}

/**
 * Read a SQL identifier ending at `endOffset` (inclusive). Supports plain
 * identifiers, `[bracketed]`, and `"quoted"`. Returns the identifier with any
 * quoting stripped, plus the offset of its first character (useful to look
 * further back for a schema prefix).
 */
function readIdentifierBackward(
  text: string,
  endOffset: number,
): { identifier: string; startOffset: number } | null {
  if (endOffset < 0) return null;

  const ch = text[endOffset];
  if (ch === ']') {
    // [bracketed] identifier
    let i = endOffset - 1;
    while (i >= 0 && text[i] !== '[') i--;
    if (i < 0) return null;
    return { identifier: text.slice(i + 1, endOffset), startOffset: i };
  }

  if (ch === '"') {
    // "quoted" identifier
    let i = endOffset - 1;
    while (i >= 0 && text[i] !== '"') i--;
    if (i < 0) return null;
    return { identifier: text.slice(i + 1, endOffset), startOffset: i };
  }

  if (ch === '`') {
    // `backtick` identifier (MySQL)
    let i = endOffset - 1;
    while (i >= 0 && text[i] !== '`') i--;
    if (i < 0) return null;
    return { identifier: text.slice(i + 1, endOffset), startOffset: i };
  }

  // Plain identifier: [A-Za-z_][A-Za-z0-9_]*
  if (!/[A-Za-z0-9_]/.test(ch)) return null;
  let i = endOffset;
  while (i >= 0 && /[A-Za-z0-9_]/.test(text[i])) i--;
  return { identifier: text.slice(i + 1, endOffset + 1), startOffset: i + 1 };
}

/**
 * Count top-level commas between `openParenOffset + 1` and `cursorOffset`,
 * skipping nested parens and string/comment regions. Returns the 0-based
 * active-parameter index for the cursor.
 */
export function countActiveParameter(
  text: string,
  openParenOffset: number,
  cursorOffset: number,
): number {
  let depth = 0;
  let commas = 0;
  let i = openParenOffset + 1;
  const end = Math.min(cursorOffset, text.length);

  while (i < end) {
    const ch = text[i];
    const next = text[i + 1];

    // Skip comments and strings — cheap local re-scan (we don't reuse the
    // mask from findEnclosingFunctionCall so this function stays callable
    // standalone).
    if (ch === '-' && next === '-') {
      while (i < end && text[i] !== '\n') i++;
      continue;
    }
    if (ch === '/' && next === '*') {
      i += 2;
      while (i < end - 1 && !(text[i] === '*' && text[i + 1] === '/')) i++;
      if (i < end) i += 2;
      continue;
    }
    if (ch === "'") {
      i++;
      while (i < end) {
        if (text[i] === "'" && text[i + 1] === "'") { i += 2; continue; }
        if (text[i] === "'") { i++; break; }
        i++;
      }
      continue;
    }
    if (ch === '"') {
      i++;
      while (i < end && text[i] !== '"') i++;
      if (i < end) i++;
      continue;
    }
    if (ch === '[') {
      i++;
      while (i < end && text[i] !== ']') i++;
      if (i < end) i++;
      continue;
    }
    if (ch === '`') {
      i++;
      while (i < end && text[i] !== '`') i++;
      if (i < end) i++;
      continue;
    }
    // Dollar-quoted string (PostgreSQL)
    if (ch === '$') {
      const tag = readDollarTag(text, i);
      if (tag !== null) {
        const closer = text.indexOf(tag, i + tag.length);
        const tagEnd = closer === -1 ? end : Math.min(closer + tag.length, end);
        i = tagEnd;
        continue;
      }
    }

    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ',' && depth === 0) commas++;

    i++;
  }

  return commas;
}

/**
 * Format the full signature label for Monaco, e.g.
 * `dbo.ufnGetProductListPrice(@ProductID int, @OrderDate datetime)`, and
 * compute the `[start, end]` character ranges for each parameter so Monaco
 * highlights the active parameter inside the label.
 */
export function buildSignatureLabel(
  metadata: RoutineMetadata,
): { label: string; parameters: Array<{ label: [number, number] }> } {
  // Built-in catalogs (sqlite, mssql) carry a schema for label purposes only
  // sometimes — mssql's static built-ins use `schema: ''` as a sentinel for
  // "no schema prefix" (SSMS shows `ISNULL(...)`, never `.ISNULL(...)`).
  const schemaPrefix = metadata.schema ? `${metadata.schema}.` : '';
  const head = `${schemaPrefix}${metadata.name}(`;
  let label = head;
  const parameters: Array<{ label: [number, number] }> = [];
  for (let i = 0; i < metadata.parameters.length; i++) {
    if (i > 0) label += ', ';
    const start = label.length;
    label += formatParameter(metadata.parameters[i]);
    const end = label.length;
    parameters.push({ label: [start, end] });
  }
  label += ')';
  return { label, parameters };
}

/**
 * Format one parameter as `@name type` with a reasonable type decoration for
 * string/numeric types. Matches the style SSMS and Azure Data Studio use.
 */
/**
 * Format one parameter for Oracle: combines DATA_TYPE with DATA_LENGTH /
 * DATA_PRECISION / DATA_SCALE and CHAR_USED into a readable label.
 */
function formatOracleParameter(param: RoutineParameter): string {
  const name = param.name || '';
  const baseType = (param.dataType || '').toUpperCase();
  let typeStr = param.dataType || '';

  const isOracleStringType = /^(VARCHAR2|NVARCHAR2|CHAR|NCHAR|RAW)$/.test(baseType);
  const isOraclePrecisionType = /^(NUMBER|FLOAT)$/.test(baseType);
  const isOracleTimestampType = /^TIMESTAMP/.test(baseType);

  if (isOracleStringType && param.maxLength != null) {
    const suffix = param.charUsed === 'C' ? ' CHAR' : param.charUsed === 'B' ? ' BYTE' : '';
    typeStr += `(${param.maxLength}${suffix})`;
  } else if (isOraclePrecisionType && param.precision != null) {
    typeStr += param.scale != null && param.scale > 0
      ? `(${param.precision},${param.scale})`
      : `(${param.precision})`;
  } else if (isOracleTimestampType && param.scale != null) {
    typeStr += `(${param.scale})`;
  }

  const out = param.isOutput ? ' OUT' : '';
  return name ? `${name} ${typeStr}${out}` : `${typeStr}${out}`;
}

export function formatParameter(param: RoutineParameter): string {
  // Oracle parameters carry charUsed for VARCHAR2(n CHAR) formatting
  if (param.charUsed !== undefined) {
    return formatOracleParameter(param);
  }

  const name = param.name || '';
  const baseType = (param.dataType || '').toLowerCase();
  let typeStr = param.dataType || '';

  const isStringType = /^(n?(var)?char|n?text|varbinary|binary)$/.test(baseType);
  const isPrecisionType = /^(decimal|numeric)$/.test(baseType);
  const isTimeType = /^(datetime2|datetimeoffset|time)$/.test(baseType);

  if (isStringType && param.maxLength != null) {
    // sys.parameters.max_length is bytes; nvarchar/nchar store 2 bytes/char.
    const isUnicode = /^n/.test(baseType);
    if (param.maxLength === -1) {
      typeStr += '(max)';
    } else {
      const chars = isUnicode ? Math.floor(param.maxLength / 2) : param.maxLength;
      typeStr += `(${chars})`;
    }
  } else if (isPrecisionType && param.precision != null) {
    typeStr += param.scale != null ? `(${param.precision}, ${param.scale})` : `(${param.precision})`;
  } else if (isTimeType && param.scale != null) {
    typeStr += `(${param.scale})`;
  }

  const out = param.isOutput ? ' OUTPUT' : '';
  return name ? `${name} ${typeStr}${out}` : `${typeStr}${out}`;
}

/**
 * Resolve routine metadata synchronously-if-warm, asynchronously otherwise.
 * Returns all overloads for the given routine (PG/Oracle may have multiple).
 *
 * Resolution order:
 * 1. Engine's schema-cache snapshot (warmed on connect — synchronous)
 * 2. Module-level per-routine cache (populated on first use — synchronous)
 * 3. IPC fallback via `get-routine-metadata` (async)
 */
async function resolveRoutine(
  schema: string,
  name: string,
  connectionId: string,
  database: string,
  engineId?: DatabaseEngineId,
  packageName?: string,
): Promise<RoutineMetadata[] | null> {
  const params = { connectionId, database, schema, name, engineId, ...(packageName ? { packageName } : {}) };
  const cached = getCachedRoutine(params);
  if (cached !== undefined) return cached;
  return fetchRoutine(params);
}

/**
 * Main entry point called by the signature-help provider in SqlMonaco.tsx.
 *
 * Returns null when: the cursor isn't inside a function call, or the engine
 * is unsupported, or the routine can't be resolved, or it has zero
 * parameters (no hints to show).
 */
export async function computeFunctionSignatureHelp(
  text: string,
  cursorOffset: number,
  connectionId: string | undefined,
  database: string | undefined,
  engineId: DatabaseEngineId,
): Promise<FunctionSignatureHelp | null> {
  if (engineId !== 'mssql' && engineId !== 'pgsql' && engineId !== 'mysql' && engineId !== 'oracle' && engineId !== 'sqlite' && engineId !== 'mock') return null;

  const ctx = findEnclosingFunctionCall(text, cursorOffset, engineId);
  if (!ctx) return null;

  let overloads: RoutineMetadata[] | null = null;

  // MSSQL built-ins: consulted BEFORE the sys.parameters catalog lookup, but
  // ONLY for unqualified or `sys.`-qualified calls — synchronous, no
  // connection required (mirrors the sqlite static-map precedent above). A
  // call qualified with any OTHER schema (`dbo.Foo(`) always falls through
  // to the catalog lookup below instead, so a user routine that happens to
  // share a built-in's name still wins when called schema-qualified — this
  // matches real SQL Server name resolution, where a recognized built-in
  // function token binds before object resolution only for unqualified
  // calls. See `functionSignatureHelp.test.ts`'s "mssql built-ins vs
  // user-routine precedence" tests for the pinned behavior.
  const mssqlBuiltinEligible =
    engineId === 'mssql' && (ctx.schema === null || ctx.schema.toLowerCase() === 'sys');
  const mssqlBuiltin = mssqlBuiltinEligible
    ? MSSQL_BUILTIN_ROUTINES.get(ctx.name.toLowerCase())
    : undefined;

  if (engineId === 'sqlite') {
    // SQLite: synchronous lookup from static built-in table (no IPC, no schema)
    const entry = SQLITE_BUILTIN_ROUTINES.get(ctx.name.toLowerCase());
    overloads = entry ? [{ schema: entry.schema, name: entry.name, parameters: entry.parameters }] : null;
  } else if (mssqlBuiltin) {
    overloads = [{ schema: mssqlBuiltin.schema, name: mssqlBuiltin.name, parameters: mssqlBuiltin.parameters }];
  } else {
    if (!connectionId || !database) return null;

    const schema = ctx.schema ?? resolveDefaultSchemaForEngine(engineId, database, connectionId);

    // For Oracle three-level identifiers (SCHEMA.PKG.PROC), we need a different
    // resolver call that includes the package name. For two-level (PKG.PROC or
    // SCHEMA.PROC), the Oracle metadataService branch does a two-step lookup.
    let resolveSchema = schema;
    let resolveName = ctx.name;
    let resolvePackage: string | undefined;
    if (engineId === 'oracle') {
      // Oracle upper-cases unquoted identifiers
      resolveSchema = schema.toUpperCase();
      resolveName = ctx.name.toUpperCase();
      if (ctx.packageName) resolvePackage = ctx.packageName.toUpperCase();
    }
    overloads = await resolveRoutine(resolveSchema, resolveName, connectionId, database, engineId, resolvePackage);
  }

  // Filter out overloads with zero parameters (no hints to show)
  const validOverloads = overloads?.filter(m => m.parameters.length > 0);
  if (!validOverloads || validOverloads.length === 0) return null;

  const rawActive = countActiveParameter(text, ctx.openParenOffset, cursorOffset);

  // Build one signature entry per overload. activeParameter is clamped per the
  // first overload (v1.1 — v1.2 will pick the best overload by arg count).
  const firstOverload = validOverloads[0];
  const activeParameter = Math.min(Math.max(rawActive, 0), firstOverload.parameters.length - 1);

  const signatures = validOverloads.map((metadata) => {
    const { label, parameters } = buildSignatureLabel(metadata);
    return {
      label,
      documentation: undefined,
      parameters: parameters.map((p, i) => ({
        label: p.label,
        documentation: metadata.parameters[i]?.dataType
          ? `${metadata.parameters[i].name} ${metadata.parameters[i].dataType}`
          : undefined,
      })),
    };
  });

  return {
    signatures,
    activeSignature: 0,
    activeParameter,
  };
}
